import { TVersion } from '@modules/documents';
import { TFileVersion } from '@modules/files';

enum ApprovalStatus {
  AUTHORING = 'AUTHORING',
  REVIEWING = 'REVIEWING',
  APPROVING = 'APPROVING',
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
  CANCELED = 'CANCELED',
}

/** The three active approval phases where a user can have pending actions */
type TApprovalPhaseKey = ApprovalStatus.AUTHORING | ApprovalStatus.REVIEWING | ApprovalStatus.APPROVING;

/** Array of active approval phases for iteration */
const APPROVAL_PHASE_KEYS: TApprovalPhaseKey[] = [
  ApprovalStatus.AUTHORING,
  ApprovalStatus.REVIEWING,
  ApprovalStatus.APPROVING,
];

enum ApprovalUserStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
}

type TApprovalUser = {
  user: string;
  status: ApprovalUserStatus;
  date?: string;
  note?: string;
  customGroupIds: string[];
};

type TApprovalPhase = {
  users: TApprovalUser[];
  dateCompleted?: string;
};

type TApprovalPhases = {
  AUTHORING?: TApprovalPhase;
  REVIEWING?: TApprovalPhase;
  APPROVING?: TApprovalPhase;
};

type TApproval = {
  _id: string;
  versionId: string;
  status: ApprovalStatus;
  phases: TApprovalPhases;
  dateOpened: string;
  creator: string;
  dateCompleted?: string;
  violationNote?: string;
  cancellationUser?: string;
};

type TUpdateApprovalReturn = {
  approval: TApproval;
  increasedVersion: boolean;
};

type TCreateApprovalReturn = {
  approval: TApproval;
  version: TVersion | TFileVersion;
};

/** User entry shape sent to the API when creating/updating approvals. */
type TApprovalUserEntry = {
  userId: string;
  customGroupIds?: string[];
};

type TApprovalUserDialogState = {
  isOpen: boolean;
  approvalUser: TApprovalUser;
  phaseName: TApprovalPhaseKey;
};

type TUpdateApprovalDialogState = {
  isOpen: boolean;
  approvalId: string;
};

type TViewApprovalDialogState = {
  isOpen: boolean;
  approvalId: string;
};

/** Legal confirmation statements shown when a user accepts an approval phase. */
const APPROVAL_STATEMENTS: Record<TApprovalPhaseKey, string> = {
  [ApprovalStatus.AUTHORING]:
    'I hereby confirm that I have authored this document and that, to the best of my knowledge, it contains no errors in its content.',
  [ApprovalStatus.REVIEWING]:
    'I hereby confirm that I have reviewed this document and that, to the best of my knowledge, it contains no errors in its content.',
  [ApprovalStatus.APPROVING]:
    'I hereby confirm that I approve this document and that, to the best of my knowledge, it contains no errors in its content.',
};

export { ApprovalStatus, ApprovalUserStatus, APPROVAL_PHASE_KEYS, APPROVAL_STATEMENTS };

export type {
  TApprovalPhaseKey,
  TApprovalUser,
  TApprovalPhase,
  TApprovalPhases,
  TApproval,
  TUpdateApprovalReturn,
  TCreateApprovalReturn,
  TApprovalUserEntry,
  TApprovalUserDialogState,
  TUpdateApprovalDialogState,
  TViewApprovalDialogState,
};
